import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useCancelChip, usePlayChip, useTeamChips } from '@/features/fantasy/hooks';
import type { ChipType, Tables } from '@/lib/supabase/types';
import { errorKey } from '@/lib/utils/errors';

const CHIP_ORDER: ChipType[] = ['triple_captain', 'bench_boost', 'free_hit', 'wildcard'];

/** Per-chip display key (camelCase) + whether scoring supports it yet. */
const CHIP_META: Record<ChipType, { i18n: string; available: boolean }> = {
  triple_captain: { i18n: 'tripleCaptain', available: true },
  bench_boost: { i18n: 'benchBoost', available: true },
  // Free Hit / Wildcard waive transfer hits - meaningful now the transfer
  // system has landed (migration 24).
  free_hit: { i18n: 'freeHit', available: true },
  wildcard: { i18n: 'wildcard', available: true },
};

type ChipState = 'play' | 'active' | 'used' | 'soon' | 'blocked' | 'locked';

export interface ChipsStripProps {
  fantasyTeamId: string | undefined;
  gameweek: Pick<Tables<'gameweeks'>, 'id' | 'deadline_at'> | null;
}

/**
 * The four single-use chips for the current gameweek: arm one before the
 * deadline (one per gameweek, each chip once per tournament). Free Hit /
 * Wildcard are shown as upcoming until the transfer system lands.
 */
export function ChipsStrip({ fantasyTeamId, gameweek }: ChipsStripProps) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const chipsQuery = useTeamChips(fantasyTeamId);
  const playChip = usePlayChip();
  const cancelChip = useCancelChip();
  const toast = useToast();
  const [now] = useState(() => Date.now());

  const chips = chipsQuery.data ?? [];
  const deadlinePassed = gameweek ? new Date(gameweek.deadline_at).getTime() <= now : true;
  const activeHere = gameweek
    ? (chips.find((chip) => chip.gameweek_id === gameweek.id) ?? null)
    : null;
  const usedTypes = new Set(chips.map((chip) => chip.chip_type));
  const busy = playChip.isPending || cancelChip.isPending;

  const stateFor = (type: ChipType): ChipState => {
    if (activeHere?.chip_type === type) return 'active';
    if (usedTypes.has(type)) return 'used';
    if (!CHIP_META[type].available) return 'soon';
    if (activeHere) return 'blocked';
    if (!fantasyTeamId || deadlinePassed) return 'locked';
    return 'play';
  };

  const onPlay = (type: ChipType) => {
    if (!gameweek) return;
    playChip.mutate(
      { gameweekId: gameweek.id, chipType: type },
      {
        onSuccess: () =>
          toast.show({
            message: t('fantasy.chips.armed', { chip: t(`fantasy.chips.${CHIP_META[type].i18n}`) }),
            tone: 'success',
          }),
        onError: (error) => toast.show({ message: t(errorKey(error)), tone: 'error' }),
      },
    );
  };

  const onCancel = () => {
    if (!gameweek) return;
    cancelChip.mutate(gameweek.id, {
      onSuccess: () => toast.show({ message: t('fantasy.chips.removed'), tone: 'info' }),
      onError: (error) => toast.show({ message: t(errorKey(error)), tone: 'error' }),
    });
  };

  return (
    <Card className="mx-4 mb-4 px-4 py-4">
      <AppText variant="heading">{t('fantasy.chips.title')}</AppText>
      <AppText variant="micro" className="mt-1 leading-5 text-muted">
        {fantasyTeamId ? t('fantasy.chips.hint') : t('fantasy.chips.noTeam')}
      </AppText>

      <View className="mt-3 flex-row flex-wrap" style={{ gap: 10 }}>
        {CHIP_ORDER.map((type) => {
          const state = stateFor(type);
          const isActive = state === 'active';
          // An active chip locks in at the deadline - it stays 'Active' but can
          // no longer be cancelled, so don't invite a doomed cancel_chip call.
          const canCancel = isActive && !deadlinePassed;
          const tappable = (state === 'play' || canCancel) && !busy;
          return (
            <Pressable
              key={type}
              accessibilityRole="button"
              disabled={!tappable}
              onPress={() => (canCancel ? onCancel() : onPlay(type))}
              style={{ width: '47%' }}
              className={`rounded-card border px-3 py-3 ${
                isActive ? 'border-volt-line bg-volt/10' : 'border-line bg-surface'
              } ${tappable ? 'active:opacity-70' : ''}`}
            >
              <View className="flex-row items-center justify-between">
                <AppText
                  variant="caption"
                  weight="bold"
                  numberOfLines={1}
                  style={{ color: isActive ? palette.voltBright : palette.text }}
                >
                  {t(`fantasy.chips.${CHIP_META[type].i18n}`)}
                </AppText>
                <ChipBadge state={state} />
              </View>
              <AppText variant="micro" numberOfLines={2} className="mt-1 leading-4 text-muted">
                {t(`fantasy.chips.${CHIP_META[type].i18n}Desc`)}
              </AppText>
            </Pressable>
          );
        })}
      </View>
    </Card>
  );
}

function ChipBadge({ state }: { state: ChipState }) {
  const { t } = useTranslation();
  const label =
    state === 'active'
      ? t('fantasy.chips.active')
      : state === 'used'
        ? t('fantasy.chips.used')
        : state === 'soon'
          ? t('fantasy.chips.soon')
          : state === 'play'
            ? t('fantasy.chips.play')
            : null;
  if (!label) return null;
  const tone =
    state === 'active'
      ? 'text-on-volt bg-volt'
      : state === 'play'
        ? 'text-volt-bright bg-volt/10'
        : 'text-muted bg-surface-2';
  return (
    <View className={`rounded-full px-2 py-0.5 ${tone}`}>
      <AppText variant="micro" weight="bold" style={{ fontSize: 9 }}>
        {label}
      </AppText>
    </View>
  );
}
